import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import { useAppStore } from '@/stores';
import type { MenuProps } from 'antd';
import {
  hasRolePermission,
  canAccessMenuA1,
  canAccessMenuA2,
  type PermissionAction,
} from '@/utils/permission';
// Import SVG icons
import MenuIcon from '@/assets/icons/Front-icon.svg';
import CollapseIcon from '@/assets/icons/sidebar-left.svg';
import UserProtectedIcon from '@/assets/icons/user-protected.svg';
import MaintenanceIcon from '@/assets/icons/maintenance.svg';
import AnnouncementIcon from '@/assets/icons/announcement.svg';
import FileTextIcon from '@/assets/icons/file-text.svg';
import BuildingIcon from '@/assets/icons/building.svg';
import OverallIcon from '@/assets/icons/overall.svg';
import BookIcon from '@/assets/icons/book.svg';
import CalendarIcon from '@/assets/icons/calendar.svg';
import ComputerIcon from '@/assets/icons/computer.svg';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

// Extend MenuItem với permission
interface MenuItemWithPermission extends Omit<MenuItem, 'children'> {
  key: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  style?: React.CSSProperties;
  permission?: PermissionAction;
  children?: MenuItemWithPermission[];
}

// Tree line component for Level 3 items
const TreeLabel: React.FC<{ label: string; isLast?: boolean }> = ({ label, isLast = false }) => (
  <span className="tree-item">
    <span className={`tree-line ${isLast ? 'tree-line-last' : ''}`} />
    <span className="tree-horizontal" />
    {label}
  </span>
);

const menuConfigA1: MenuItemWithPermission[] = [
  {
    key: 'admin-accounts',
    icon: <img src={UserProtectedIcon} alt="user-protected-icon" width={24} height={24} />,
    label: '管理アカウント',
    permission: 'menu:temples',
    children: [
      {
        key: '/admin-accounts/list',
        label: 'アカウント一覧',
        permission: 'page:admin-accounts:list',
      },
      {
        key: '/admin-accounts/create',
        label: 'アカウント登録',
        permission: 'page:admin-accounts:create',
      },
    ],
  },
  {
    key: 'maintenance',
    icon: <img src={MaintenanceIcon} alt="maintenance-icon" width={24} height={24} />,
    label: 'メンテナンス',
    permission: 'menu:maintenance',
    children: [
      {
        key: '/maintenance/list',
        label: 'メンテナンス一覧',
        permission: 'page:maintenance:list',
      },
      {
        key: '/maintenance/create',
        label: 'メンテナンス情報登録',
        permission: 'page:maintenance:create',
      },
    ],
  },
  {
    key: 'notifications',
    icon: <img src={AnnouncementIcon} alt="maintenance-icon" width={24} height={24} />,
    label: 'お知らせ',
    permission: 'menu:notifications',
    children: [
      {
        key: '/notifications/list',
        label: 'お知らせ一覧',
        permission: 'page:notifications:list',
      },
      {
        key: '/notifications/create',
        label: 'お知らせ情報登録',
        permission: 'page:notifications:create',
      },
    ],
  },
  {
    key: '/data-viewing',
    icon: <img src={FileTextIcon} alt="maintenance-icon" width={24} height={24} />,
    label: 'データ閲覧',
    permission: 'menu:data-viewing',
  },
  {
    key: '/enterprise-configuration',
    icon: <img src={BuildingIcon} alt="maintenance-icon" width={24} height={24} />,
    label: 'エンタープライズ構成',
    permission: 'menu:enterprise-configuration',
  },
];
const menuConfigA2: MenuItemWithPermission[] = [
  {
    key: 'overall',
    icon: <img src={OverallIcon} alt="tree-icon" width={24} height={24} />,
    label: '全体',
    permission: 'menu:overall',
    children: [
      {
        key: 'admin-accounts',
        label: '管理アカウント',
        permission: 'menu:admin-accounts-sub',
        children: [
          {
            key: '/admin-accounts/list',
            label: <TreeLabel label="アカウント一覧" />,
            permission: 'page:admin-accounts:list',
          },
          {
            key: '/admin-accounts/create',
            label: <TreeLabel label="アカウント登録" isLast />,
            permission: 'page:admin-accounts:create',
          },
        ],
      },
      {
        key: 'users',
        label: '会員（ユーザー）',
        permission: 'menu:users',
        children: [
          {
            key: '/users/list',
            label: <TreeLabel label="ユーザー一覧" />,
            permission: 'page:users:list',
          },
          {
            key: '/users/create',
            label: <TreeLabel label="ユーザー情報登録" />,
            permission: 'page:users:create',
          },
          {
            key: '/family-users/list',
            label: <TreeLabel label="家族データ一覧" />,
            permission: 'page:family-users:list',
          },
          {
            key: '/family-users/create',
            label: <TreeLabel label="家族データ登録" isLast />,
            permission: 'page:family-users:create',
          },
        ],
      },
      {
        key: 'notifications',
        label: 'お知らせ',
        permission: 'menu:notifications-sub',
        children: [
          {
            key: '/notifications/list',
            label: <TreeLabel label="お知らせ一覧" />,
            permission: 'page:notifications:list',
          },
          {
            key: '/notifications/create',
            label: <TreeLabel label="お知らせ情報登録" />,
            permission: 'page:notifications:create',
          },
          {
            key: '/startup-messages/list',
            label: <TreeLabel label="起動画面メッセージー覧" />,
            permission: 'page:startup-messages:list',
          },
          {
            key: '/startup-messages/create',
            label: <TreeLabel label="起動画面メッセージ登録" isLast />,
            permission: 'page:startup-messages:create',
          },
        ],
      },
      {
        key: 'point',
        label: '拠点',
        permission: 'menu:point',
        children: [
          {
            key: '/point/list',
            label: <TreeLabel label="拠点一覧" />,
            permission: 'page:point:list',
          },
          {
            key: '/point/create',
            label: <TreeLabel label="拠点情報登録" isLast />,
            permission: 'page:point:create',
          },
        ],
      },
    ],
  },
  {
    key: 'content',
    icon: <img src={FileTextIcon} alt="tree-icon" width={24} height={24} />,
    label: 'コンテンツ',
    permission: 'menu:content',
    children: [
      {
        key: 'movies',
        label: '動画',
        permission: 'menu:movies',
        children: [
          {
            key: '/movies/list',
            label: <TreeLabel label="動画一覧" />,
            permission: 'page:movies:list',
          },
          {
            key: '/movies/create',
            label: <TreeLabel label="動画情報登録" isLast />,
            permission: 'page:movies:create',
          },
        ],
      },
      {
        key: 'video-category',
        label: '動画カテゴリー',
        permission: 'menu:video-category',
        children: [
          {
            key: '/video-category/list',
            label: <TreeLabel label="動画カテゴリー一覧" />,
            permission: 'page:video-category:list',
          },
          {
            key: '/video-category/create',
            label: <TreeLabel label="動画カテゴリー登録" isLast />,
            permission: 'page:video-category:create',
          },
        ],
      },
      {
        key: 'copy-sutra',
        label: '写経',
        permission: 'menu:copy-sutra',
        children: [
          {
            key: '/copy-sutra/list',
            label: <TreeLabel label="写経一覧" />,
            permission: 'page:copy-sutra:list',
          },
          {
            key: '/copy-sutra/create',
            label: <TreeLabel label="写経情報登録" isLast />,
            permission: 'page:copy-sutra:create',
          },
        ],
      },
      {
        key: 'morning-sutra',
        label: '朝勤行用お経',
        permission: 'menu:morning-sutra',
        children: [
          {
            key: '/morning-sutra/list',
            label: <TreeLabel label="朝勤行用お経一覧" />,
            permission: 'page:morning-sutra:list',
          },
          {
            key: '/morning-sutra/create',
            label: <TreeLabel label="朝勤行用お経情報登録" />,
            permission: 'page:morning-sutra:create',
          },
          {
            key: '/sutra/list',
            label: <TreeLabel label="お経一覧" />,
            permission: 'page:sutra:list',
          },
          {
            key: '/sutra/create',
            label: <TreeLabel label="お経情報登録" isLast />,
            permission: 'page:sutra:create',
          },
        ],
      },
      {
        key: 'columns',
        label: 'コラム',
        permission: 'menu:columns',
        children: [
          {
            key: '/columns/list',
            label: <TreeLabel label="コラム一覧" />,
            permission: 'page:columns:list',
          },
          {
            key: '/columns/create',
            label: <TreeLabel label="コラム登録" isLast />,
            permission: 'page:columns:create',
          },
        ],
      },
      {
        key: 'column-category',
        label: 'コラムジャンル',
        permission: 'menu:column-category',
        children: [
          {
            key: '/column-category/list',
            label: <TreeLabel label="コラムジャンル一覧" />,
            permission: 'page:column-category:list',
          },
          {
            key: '/column-category/create',
            label: <TreeLabel label="コラムジャンル登録" isLast />,
            permission: 'page:column-category:create',
          },
        ],
      },
    ],
  },
  {
    key: 'courses',
    icon: <img src={BookIcon} alt="tree-icon" width={24} height={24} />,
    label: '講座',
    permission: 'menu:courses',
    children: [
      {
        key: 'course-schedules',
        label: '講座スケジュール',
        permission: 'menu:course-schedule',
        children: [
          {
            key: '/course/list',
            label: <TreeLabel label="講座一覧" />,
            permission: 'page:courses:list',
          },
          {
            key: '/course/create',
            label: <TreeLabel label="講座情報登録" />,
            permission: 'page:courses:create',
          },
          {
            key: '/course-participant/list',
            label: <TreeLabel label="講座参加者一覧" isLast />,
            permission: 'page:course-participant:list',
          },
        ],
      },
      {
        key: 'course-categories',
        label: '講座カテゴリー',
        permission: 'menu:course-category',
        children: [
          {
            key: '/course-category/list',
            label: <TreeLabel label="講座カテゴリー一覧" />,
            permission: 'page:course-category:list',
          },
          {
            key: '/course-category/create',
            label: <TreeLabel label="講座カテゴリー情報登録" />,
            permission: 'page:course-category:create',
          },
        ],
      },
    ],
  },
  {
    key: 'reservations',
    icon: <img src={CalendarIcon} alt="tree-icon" width={24} height={24} />,
    label: '予約',
    permission: 'menu:reservations',
    children: [
      {
        key: 'counselor-online',
        label: 'オンライン相談担当者',
        permission: 'menu:counselor-online',
        children: [
          {
            key: '/counselor-online/list',
            label: <TreeLabel label="オンライン相談担当者" />,
            permission: 'page:counselor-online:list',
          },
          {
            key: '/counselor-online/create',
            label: <TreeLabel label="オンライン相談担当者登録" isLast />,
            permission: 'page:counselor-online:create',
          },
        ],
      },
      {
        key: 'counselor-online-type',
        label: 'オンライン相談ジャンル',
        permission: 'menu:counselor-online-type',
        children: [
          {
            key: '/counselor-online-type/list',
            label: <TreeLabel label="オンライン相談ジャンル一覧" />,
            permission: 'page:counselor-online-type:list',
          },
          {
            key: '/counselor-online-type/create',
            label: <TreeLabel label="オンライン相談ジャンル登録" isLast />,
            permission: 'page:counselor-online-type:create',
          },
        ],
      },
      {
        key: 'counselor-online-schedule',
        label: 'オンライン相談スケジュール',
        permission: 'menu:counselor-online-schedule',
        children: [
          {
            key: '/counselor-online-schedule/setting',
            label: <TreeLabel label="オンライン相談情報設定" isLast />,
            permission: 'page:counselor-online-schedule:setting',
          },
          {
            key: '/counselor-online-schedule/setting',
            label: <TreeLabel label="オンライン相談情報詳細 (相談スケジュールの詳細)" isLast />,
            permission: 'page:counselor-online-schedule:detail',
          },
        ],
      },
      {
        key: 'bus-timetable',
        label: 'バス時刻表',
        permission: 'menu:bus-timetable',
        children: [
          {
            key: '/bus-timetable/list',
            label: <TreeLabel label="バス時刻表一覧" />,
            permission: 'page:bus-timetable:list',
          },
          {
            key: '/bus-timetable/create',
            label: <TreeLabel label="バス時刻表登録" isLast />,
            permission: 'page:bus-timetable:create',
          },
        ],
      },
    ],
  },
  {
    key: 'devices',
    icon: <img src={ComputerIcon} alt="tree-icon" width={24} height={24} />,
    label: 'デバイス管理',
    permission: 'menu:devices',
    children: [
      {
        key: '/devices/create',
        label: 'エンロールメント作成',
        permission: 'page:devices:create',
      },
      {
        key: '/devices/list',
        label: 'タブレット一覧',
        permission: 'page:devices:list',
      },
    ],
  },
];
const filterMenuByPermission = (items: MenuItemWithPermission[]): MenuItem[] => {
  return items
    .filter(item => !item.permission || hasRolePermission(item.permission))
    .map(item => {
      const { permission, ...rest } = item;
      if (item.children) {
        const filteredChildren = filterMenuByPermission(item.children);
        if (filteredChildren.length === 0) return null;
        return { ...rest, children: filteredChildren };
      }
      return rest;
    })
    .filter(Boolean) as MenuItem[];
};

const getMenuItems = (): MenuItem[] => {
  if (canAccessMenuA1()) {
    return filterMenuByPermission(menuConfigA1);
  }
  if (canAccessMenuA2()) {
    return filterMenuByPermission(menuConfigA2);
  }
  return filterMenuByPermission(menuConfigA2);
};

const getActiveMenuKeys = (
  pathname: string,
  items: MenuItemWithPermission[]
): { selectedKeys: string[]; openKeys: string[] } => {
  const selectedKeys: string[] = [];
  const openKeys: string[] = [];

  const findKeys = (menuItems: MenuItemWithPermission[], parentKeys: string[] = []): boolean => {
    for (const item of menuItems) {
      if (item.key === pathname) {
        selectedKeys.push(item.key);
        openKeys.push(...parentKeys);
        return true;
      }

      if (item.children) {
        const found = findKeys(item.children, [...parentKeys, item.key]);
        if (found) return true;
      }
    }
    return false;
  };

  findKeys(items);
  return { selectedKeys, openKeys };
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar } = useAppStore();
  const menuType = localStorage.getItem('menuType') || 'A1';
  const currentMenuConfig = useMemo(() => {
    return canAccessMenuA1() ? menuConfigA1 : menuConfigA2;
  }, [menuType]);
  const menuItems = useMemo(() => getMenuItems(), [menuType]);
  const { selectedKeys, openKeys: defaultOpenKeys } = useMemo(
    () => getActiveMenuKeys(location.pathname, currentMenuConfig),
    [location.pathname]
  );
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys);
  useEffect(() => {
    setOpenKeys(defaultOpenKeys);
  }, [defaultOpenKeys]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={sidebarCollapsed}
      width={300}
      collapsedWidth={80}
      className="sidebar-container"
    >
      {/* Header */}
      <div className="sidebar-header">
        <button onClick={toggleSidebar} className="menu-toggle">
          <img src={MenuIcon} alt="Menu" width={24} height={24} />
        </button>
        {!sidebarCollapsed && <span className="sidebar-title">メニュー</span>}
        {!sidebarCollapsed && (
          <button className="collapse-btn" onClick={toggleSidebar}>
            <img src={CollapseIcon} alt="Collapse" width={24} height={24} />
          </button>
        )}
      </div>

      {/* Menu */}
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={handleMenuClick}
        className="sidebar-menu"
      />
    </Sider>
  );
};

export default Sidebar;
